import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle,
  UserPlus, RefreshCcw, XCircle, Phone, Mail,
} from "lucide-react";
import { API, useAuth } from "../App";

/**
 * Admin: bulk-register/update EMPLOYEES from XLSX/XLS with columns
 *   MATRICULA, NOME, CPF, POSTO, DATA DE ADMISSÃO
 * Matches by MATRICULA (primary) then CPF (fallback). Creates if missing.
 */
export default function ImportRosterDialog({ open, onOpenChange, onImported }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setFile(null); setBusy(false); setReport(null); };
  const handleClose = (next) => {
    if (busy) return;
    if (!next) reset();
    onOpenChange(next);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/admin/employees/import-roster`,
        fd,
        {
          timeout: 180000,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setReport(res.data);
      const msg = `${res.data.created || 0} criado(s), ${res.data.updated || 0} atualizado(s)`;
      toast.success(`Importação concluída: ${msg}`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao importar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="import-roster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <UserPlus className="w-5 h-5 text-emerald-600" />
            Importar Cadastro de Funcionários
          </DialogTitle>
          <DialogDescription>
            Suba uma planilha (.xlsx ou .xls) com as colunas{" "}
            <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">
              MATRICULA, NOME, CPF, POSTO, DATA DE ADMISSÃO, EMAIL, TELEFONE
            </span>
            . Funcionários novos serão criados; existentes (por matrícula ou CPF) serão atualizados. Email e telefone são opcionais.
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {!report ? (
            <motion.div key="upload" initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <label htmlFor="roster-file"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`block cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  dragOver ? "border-emerald-500 bg-emerald-50"
                           : "border-slate-300 hover:border-slate-400 bg-slate-50"
                }`}
                data-testid="import-roster-dropzone">
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="font-medium text-slate-700">
                  {file ? file.name : "Arraste o arquivo aqui ou clique para selecionar"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  XLS ou XLSX • máximo 20 MB
                </p>
                <input id="roster-file" type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="import-roster-input" />
              </label>
              <div className="mt-3 text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Cabeçalhos aceitos:</strong> MATRICULA / MAT / CHAPA · NOME / FUNCIONARIO · CPF · POSTO / SETOR / LOCAL · DATA DE ADMISSÃO / ADMISSÃO · EMAIL / E-MAIL · TELEFONE / CELULAR / FONE / WHATSAPP
                <br /><span className="text-[10px] text-slate-500">
                  Telefones BR são normalizados automaticamente para o formato <code>+55DDDNNNNNNNN</code>.
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div key="report" initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <ReportStat icon={<UserPlus className="w-4 h-4 text-emerald-600" />}
                  label="Criados" value={report.created || 0} tone="emerald" />
                <ReportStat icon={<RefreshCcw className="w-4 h-4 text-sky-600" />}
                  label="Atualizados" value={report.updated || 0} tone="sky" />
                <ReportStat icon={<XCircle className="w-4 h-4 text-red-600" />}
                  label="Erros / Pulados" value={(report.errors || []).length + (report.skipped || 0)}
                  tone="red" />
              </div>
              {(report.phones_set > 0 || report.emails_set > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  <ReportStat icon={<Phone className="w-4 h-4 text-amber-600" />}
                    label="Telefones gravados" value={report.phones_set || 0} tone="amber" />
                  <ReportStat icon={<Mail className="w-4 h-4 text-violet-600" />}
                    label="E-mails gravados" value={report.emails_set || 0} tone="violet" />
                </div>
              )}
              {report.errors && report.errors.length > 0 && (
                <div className="border border-red-200 rounded-md">
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-red-200 text-sm font-medium text-red-700 bg-red-50">
                    <AlertTriangle className="w-4 h-4" />
                    Linhas com erro ({report.errors.length})
                  </div>
                  <div className="max-h-44 overflow-auto p-2 text-xs space-y-0.5">
                    {report.errors.map((e, i) => (
                      <p key={i} className="font-mono text-slate-700">
                        Linha {e.row}: {e.matricula ? `[${e.matricula}] ` : ""}{e.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              {report.created_list && report.created_list.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-emerald-700 font-medium">
                    Mostrar funcionários criados ({report.created_list.length})
                  </summary>
                  <div className="mt-2 max-h-40 overflow-auto bg-slate-50 rounded p-2 space-y-0.5">
                    {report.created_list.map((c, i) => (
                      <p key={i} className="font-mono">
                        [{c.matricula}] {c.nome}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <DialogFooter>
          {!report ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={busy}>
                Cancelar
              </Button>
              <Button onClick={upload} disabled={!file || busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="import-roster-submit">
                {busy ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Importando…</>)
                      : (<><Upload className="w-4 h-4 mr-1" />Importar</>)}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleClose(false)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-4 h-4 mr-1" />Concluir
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ReportStat = ({ icon, label, value, tone }) => (
  <div className={`rounded-md border p-3 bg-${tone}-50 border-${tone}-200`}>
    <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-1">
      {icon}{label}
    </div>
    <div className={`text-2xl font-bold text-${tone}-700`}>{value}</div>
  </div>
);
